/**
 * AIAvatar — bare AI identity mark. No bounding box, no container.
 *
 * Renders the brand 4-pointed star SVG (pink → orange gradient with yellow
 * shimmer) at a fixed size. Use anywhere the AI needs a distinct visual
 * identity: chat threads, empty states, agent headers, typing indicators.
 *
 * Dependencies: @/lib/utils (cn)
 */

import { cn } from "@/lib/utils"

type AIAvatarSize = "sm" | "md" | "lg"

const SIZE_MAP: Record<AIAvatarSize, number> = {
  sm: 16,
  md: 24,
  lg: 40,
}

// Stable gradient IDs — safe because all instances share identical gradient data.
const G0 = "ai-avatar-grad-primary"
const G1 = "ai-avatar-grad-shimmer"

interface AIAvatarProps {
  size?: AIAvatarSize
  className?: string
}

export function AIAvatar({ size = "md", className }: AIAvatarProps) {
  const dim = SIZE_MAP[size]

  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={cn("shrink-0", className)}
    >
      <defs>
        <linearGradient id={G0} x1="17.1875" y1="12.1875" x2="62.0313" y2="65" gradientUnits="userSpaceOnUse">
          <stop stopColor="#E81F76" stopOpacity="0.8" />
          <stop offset="1" stopColor="#EB5324" />
        </linearGradient>
        <linearGradient id={G1} x1="46.5625" y1="54.2188" x2="62.9687" y2="78.2813" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFC208" stopOpacity="0" />
          <stop offset="1" stopColor="#FFC208" />
        </linearGradient>
      </defs>
      <path
        d="M40 5C40 24.33 55.67 40 75 40C55.67 40 40 55.67 40 75C40 55.67 24.33 40 5 40C24.33 40 40 24.33 40 5Z"
        fill={`url(#${G0})`}
      />
      <path
        d="M40 5C40 24.33 55.67 40 75 40C55.67 40 40 55.67 40 75C40 55.67 24.33 40 5 40C24.33 40 40 24.33 40 5Z"
        fill={`url(#${G1})`}
        fillOpacity="0.24"
      />
    </svg>
  )
}

/**
 * RULES
 *
 * When to use:
 *   - AI message avatar in chat threads (left-side identity mark)
 *   - Empty state headers when the AI is the actor
 *   - Agent or copilot identity badges
 *   - Typing / thinking indicators
 *
 * When NOT to use:
 *   - As a loading spinner (use Spinner)
 *   - As a decorative background element without context
 *
 * Key rules:
 *   - No bounding box, no circle, no background — bare SVG only
 *   - Never wrap in a container with bg-* or border-* classes
 *   - Gradient colors are hardcoded brand values — never override
 *   - aria-hidden; always pair with a visible text label for screen readers
 *   - Three sizes only: sm (16px), md (24px), lg (40px)
 *
 * USAGE
 *   <AIAvatar />
 *   <AIAvatar size="lg" />
 *   <div className="flex items-center gap-3">
 *     <AIAvatar size="sm" />
 *     <span className="text-sm font-medium text-foreground">AI Assistant</span>
 *   </div>
 */
